<template>
  <div>
    <h6>■マッチング設定</h6>
    <job-content-table title="個人プロフィール">
      <job-content-table-outline
        title="性別"
        :target-id="targetIdMap.matching.profile.gender"
        :value="matching.gender"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.gender)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="年齢"
        :target-id="targetIdMap.matching.profile.age"
        :value="age"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.age)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="現住所"
        :target-id="targetIdMap.matching.profile.address"
        :value="address"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.address)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="最終学歴"
        :target-id="targetIdMap.matching.profile.educationalBackground"
        :value="educationalBackground"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.educationalBackground)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="卒業年月"
        :target-id="targetIdMap.matching.profile.graduate"
        :value="graduate"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.graduate)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="学校名・学部名"
        :comment-highlights="getAnyCommentHighlights(targetIdMap.matching.profile.educationalSearch.prefix)"
        :highlight-mode="highlightMode"
        data-test="educationSearch"
        v-on="$listeners"
      >
        <template #body>
          <span>「学校名」「学部／学科名」から、フリーワード検索をおこないます。（200文字以内）</span><br>
          <span
            :id="targetIdMap.matching.profile.educationalSearch.word"
            class="pega-break"
            v-text="matching.educationalSearchWord || '未登録'"
          /><br>
          <span
            :id="targetIdMap.matching.profile.educationalSearch.label"
            class="pega-break"
            v-text=" matching.educationSearchLabel || '未登録' "
          />
        </template>
      </job-content-table-outline>
      <job-content-table-outline
        title="学部・学科コード"
        :target-id="targetIdMap.matching.profile.educationDepartment"
        :value="educationDepartment"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.educationDepartment)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline-sub
        title="現在の就業状況"
        :target-ids="targetIdMap.matching.profile.work.list"
        :contents="work"
        :comment-highlights="getAnyCommentHighlights(targetIdMap.matching.profile.work.prefix)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="転居を伴う転職可否"
        :target-id="targetIdMap.matching.profile.homeMoving"
        :value="matching.homeMoving"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.homeMoving)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="未既婚"
        :target-id="targetIdMap.matching.profile.married"
        :value="matching.married"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.married)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="子供有無"
        :target-id="targetIdMap.matching.profile.child"
        :value="matching.child"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.child)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="転職回数"
        :target-id="targetIdMap.matching.profile.changeJobCount"
        :value="matching.changeJobCount"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.profile.changeJobCount)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
    </job-content-table>

    <job-content-table title="経験職種・業種">
      <job-content-table-outline-sub
        title="経験職種"
        :target-ids="targetIdMap.matching.experienced.occupation.list"
        :contents="matching.experiencedOccupationList"
        :comment-highlights="getAnyCommentHighlights(targetIdMap.matching.experienced.occupation.prefix)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="経験業種"
        :target-id="targetIdMap.matching.experienced.business"
        :value="experiencedBusiness"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.experienced.business)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
    </job-content-table>

    <job-content-table title="語学力">
      <job-content-table-outline
        title="TOEIC"
        :target-id="targetIdMap.matching.language.toeic"
        :value="toeic"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.language.toeic)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="TOEFL"
        :target-id="targetIdMap.matching.language.toefl"
        :value="toefl"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.language.toefl)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="英語スキル"
        :target-id="targetIdMap.matching.language.english"
        :value="matching.englishSkill.level"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.language.english)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
      <job-content-table-outline
        title="英語以外の語学スキル"
        :target-id="targetIdMap.matching.language.other"
        :value="otherLanguage"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.language.other)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
    </job-content-table>

    <job-content-table title="スキルシート・資格">
      <template v-for="skillMaster in skillsMaster">
        <template v-if="hasChildCategorySkillBy(skillMaster.id)">
          <job-content-table-outline-sub
            :key="skillMaster.id"
            :title="skillMaster.name"
            :target-ids="targetIdMap.matching.skill[skillMaster.id].list"
            :contents="skill[skillMaster.id]"
            :comment-highlights="getAnyCommentHighlights(targetIdMap.matching.skill[skillMaster.id].prefix)"
            :highlight-mode="highlightMode"
            v-on="$listeners"
          />
        </template>
        <template v-else>
          <job-content-table-outline
            :key="skillMaster.id"
            :title="skillMaster.name"
            :target-id="targetIdMap.matching.skill[skillMaster.id]"
            :value="skill[skillMaster.id]"
            :comment-highlights="getCommentHighlights(targetIdMap.matching.skill[skillMaster.id])"
            :highlight-mode="highlightMode"
            v-on="$listeners"
          />
        </template>
      </template>
    </job-content-table>

    <job-content-table title="保有資格">
      <job-content-table-outline
        title="保有資格"
        :target-id="targetIdMap.matching.license"
        :value="license"
        :comment-highlights="getCommentHighlights(targetIdMap.matching.license)"
        :highlight-mode="highlightMode"
        v-on="$listeners"
      />
    </job-content-table>
    <!-- 志向の指定 -->
    <job-matching-work-styles
      :matching="matching"
      :comment-highlights="getAnyCommentHighlights(targetIdMap.matching.workStyle.prefix)"
      :highlight-mode="highlightMode"
      v-on="$listeners"
    />
    <!-- 希望の指定 -->
    <job-matching-desired
      :matching="matching"
      :comment-highlights="getArrayAnyCommentHighlights(prefixes.matchingDesired)"
      :highlight-mode="highlightMode"
      v-on="$listeners"
    />
  </div>
</template>

<script>
import JobContentTable from '@/components/atoms/JobContentTable'
import JobContentTableOutline from '@/components/atoms/JobContentTableOutline'
import JobContentTableOutlineSub from '@/components/molecules/JobContentTableOutlineSub'
import JobMatchingWorkStyles from '@/components/molecules/JobMatchingWorkStyles'
import JobMatchingDesired from '@/components/molecules/JobMatchingDesired'
import jobPreviewCommon from '@/lib/jobPreviewCommon'
import jobPreviewHighlight from '@/lib/jobPreviewHighlight'
import jobMatchingCommon from '@/lib/jobMatchingCommon'

export default {
  components: {
    JobContentTable,
    JobContentTableOutline,
    JobContentTableOutlineSub,
    JobMatchingWorkStyles,
    JobMatchingDesired

  },
  mixins: [jobPreviewCommon, jobPreviewHighlight, jobMatchingCommon],
  props: {
    matching: {
      type: Object,
      required: true
    },
    commentHighlights: {
      type: Array,
      default: () => []
    },
    highlightMode: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    prefixes () {
      return {
        matchingDesired: [
          this.targetIdMap.matching.desired.prefix,
          this.targetIdMap.matching.freeWord.prefix,
          this.targetIdMap.matching.registrationDate
        ]
      }
    }
  }
}
</script>
